import { Check, TriangleAlert } from 'lucide-react';
import { useRenal, useMonitoring } from '@/store/selectors';
import { Collapsible } from '@/components/shared/Collapsible';
import { renalStabilityLabel } from '@/utils/format';

/** Duration-tiered renal monitoring with escalation (PSD §8.2 / TODO Task 2.19). */
export function RenalMonitoring(): JSX.Element | null {
  const renal = useRenal();
  const monitoring = useMonitoring();

  if (!monitoring) {
    return (
      <section className="panel" aria-label="Renal monitoring" data-testid="renal-monitoring">
        <h2 className="panel-title">Renal &amp; Duration Monitoring</h2>
        <div className="empty-state">Complete patient inputs to see monitoring recommendations, or use <strong>Load Demo</strong> (top right) for a worked example.</div>
      </section>
    );
  }

  return (
    <section className="panel panel-result" aria-label="Renal monitoring" data-testid="renal-monitoring">
      <h2 className="panel-title">Renal &amp; Duration Monitoring</h2>
      <div className="value-row">
        <span className="value-label">SCr/BUN frequency</span>
        <span className="value-num">q{monitoring.scrBunFrequencyDays}d</span>
      </div>
      {monitoring.scrBunFrequencyReason && (
        <div className="hint-text">{monitoring.scrBunFrequencyReason}</div>
      )}

      {renal && (
        <div className="value-row" title="Stable: ΔSCr ≤ 0.3 mg/dL within 24 h. Unstable: larger change requires daily SCr/BUN monitoring.">
          <span className="value-label">Renal stability</span>
          <span className={`badge ${renal.stability === 'stable' ? 'badge-safe' : 'badge-warn'}`}>
            {renal.stability === 'stable'
              ? <Check size={12} strokeWidth={2.5} aria-hidden="true" />
              : <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />}
            {renalStabilityLabel(renal.stability)}
          </span>
        </div>
      )}

      {monitoring.durationTierEscalation && monitoring.durationTierEscalation.length > 0 && (
        <Collapsible title="Duration escalation tiers" defaultOpen={true}>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Days</th>
                  <th>SCr/BUN freq</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {monitoring.durationTierEscalation.map((t, i) => (
                  <tr key={i}>
                    <td>{t.dayRange[0]}–{t.dayRange[1]}</td>
                    <td>q{t.scrBunFrequencyDays}d</td>
                    <td>{t.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {monitoring.reassessmentTriggers.length > 0 && (
        <Collapsible title="Reassessment triggers" defaultOpen={true}>
          <ul className="bullet-list">
            {monitoring.reassessmentTriggers.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </Collapsible>
      )}
    </section>
  );
}
